// OpenAPI 3.1 → operation model parser for the palbe-gen typed-client codegen.
// 1:1 TypeScript port of the CLI's Go parser (internal/backend/swiftgen.go —
// dialect-neutral despite the historical "swift" name). Behavior parity with
// the Go emitter is locked by the M1 cross-binding golden test.

// --- Parsed model -----------------------------------------------------------

export type SchemaKind =
  | 'string'
  | 'number'
  | 'integer'
  | 'boolean'
  | 'object'
  | 'array'
  | 'enum'
  | 'literal'
  | 'union'
  | 'null'
  | 'any';

export interface Schema {
  kind: SchemaKind;
  nullable: boolean;
  /** object */
  props: Prop[];
  /** array */
  elem?: Schema;
  /** An array constrained to the single JSON value []. */
  empty?: true;
  /** JSON Schema anyOf/oneOf alternatives. */
  members?: Schema[];
  literal?: string | number | boolean;
  /** Object index signature; false means no additional properties. */
  additional?: Schema | false;
  /** enum */
  enumVals: string[];
}

export interface Prop {
  name: string;
  schema: Schema;
  required: boolean;
}

export interface Op {
  operationId: string;
  method: string;
  path: string;
  /** `{name}` path segments in path order → leading string method args */
  pathParams: string[];
  input?: Schema;
  output?: Schema;
  /** declared request headers (parameters[in:header]) */
  headers?: Schema;
  /** declared query params (parameters[in:query]) */
  query?: Schema;
  /** inferred errors via the `x-palbase-errors` extension */
  errors: ErrorDef[];
  /** `x-palbase-sse` — the response is a stream of frames, not one document. */
  sse: boolean;
}
// ponytail: the Go parser also reads `x-palbase-upload` — the TS emitter never
// consumes it (web has no generated upload surface), so it is not parsed here.
//
// `x-palbase-sse` IS parsed here, and the difference is not an inconsistency.
// Skipping upload costs web nothing: there is no generated upload surface to be
// wrong. Skipping SSE would cost it everything — the method would still be
// generated, would read the streaming body ONCE, and would hand back the
// concatenation of every frame decoded as a single document. It would compile,
// it would run, and it would be silently wrong. Web is a first-class consumer of
// streaming, so the marker is consumed.

export interface ErrorDef {
  /** lowerCamel error name (e.g. "todoLocked") — class-name stem */
  name: string;
  /** wire `error` value (e.g. "todo_locked") — matched at decode time */
  code: string;
  /** HTTP status — kept for doc-comments */
  status: number;
  description: string;
  /** undefined when the error carries no payload */
  data?: Schema;
}

type JsonObject = Record<string, unknown>;

function asObject(v: unknown): JsonObject | undefined {
  if (typeof v === 'object' && v !== null && !Array.isArray(v)) return v as JsonObject;
  return undefined;
}

function asString(v: unknown): string {
  return typeof v === 'string' ? v : '';
}

/**
 * readSpecDeploy returns the document's `x-palbase-deploy` — the DEPLOY IDENTITY
 * the runtime stamps into every artifact it ships.
 *
 * Codegen prints it so "generated successfully" says WHICH contract it
 * generated from. The origin can legitimately serve the previous deploy for a
 * few seconds after a green one (a warm isolate re-reads its ACTIVE pointer on a
 * ~10s boundary), and a success line that hides that is how a client for 28
 * routes shipped after a deploy of 29 — caught only by counting operations by
 * hand.
 *
 * It is deliberately NOT `info.version`: OpenAPI requires that field, so it
 * always holds something and cannot express "this runtime does not know". The
 * extension is omitted instead, so absence is the answer and no placeholder
 * needs recognising.
 *
 * Returns '' when the document names no deploy, or is unreadable. It never
 * throws: parseOpenAPI owns the loud failure for a malformed document, and a
 * provenance note must not become a second, competing error path.
 */
export function readSpecDeploy(specText: string): string {
  try {
    return asString(asObject(JSON.parse(specText))?.['x-palbase-deploy']);
  } catch {
    return '';
  }
}

// --- Parse ------------------------------------------------------------------

export function parseOpenAPI(specText: string): Op[] {
  let root: JsonObject | undefined;
  try {
    root = asObject(JSON.parse(specText));
  } catch (e) {
    throw new Error(`openapi.json is not valid JSON: ${e instanceof Error ? e.message : e}`);
  }
  const paths = asObject(root?.paths);
  if (!paths) {
    throw new Error('openapi.json has no `paths`');
  }

  const ops: Op[] = [];
  for (const [path, item] of Object.entries(paths)) {
    const methods = asObject(item);
    if (!methods) continue;
    for (const [method, raw] of Object.entries(methods)) {
      const op = asObject(raw);
      if (!op) continue;
      const opId = asString(op.operationId);
      if (opId === '') continue;
      ops.push({
        operationId: opId,
        method: method.toUpperCase(),
        path: normalizePathTemplate(path),
        pathParams: pathParamNames(path),
        input: requestSchema(op),
        output: responseSchema(op),
        headers: parametersSchemaIn(op, 'header'),
        query: parametersSchemaIn(op, 'query'),
        errors: declaredErrors(op),
        sse: op['x-palbase-sse'] !== undefined,
      });
    }
  }
  ops.sort((a, b) => (a.operationId < b.operationId ? -1 : a.operationId > b.operationId ? 1 : 0));
  return ops;
}

// declaredErrors reads the `x-palbase-errors` OpenAPI extension the backend
// runtime stashes on each operation. Returns [] when no errors were inferred.
function declaredErrors(op: JsonObject): ErrorDef[] {
  const extRaw = asObject(op['x-palbase-errors']);
  if (!extRaw) return [];
  const responses = asObject(op.responses);
  const out: ErrorDef[] = [];
  for (const [name, raw] of Object.entries(extRaw)) {
    const entry = asObject(raw);
    if (!entry) continue;
    const status = typeof entry.status === 'number' ? Math.trunc(entry.status) : 0;
    const code = asString(entry.code);
    const description = asString(entry.description);
    const hasData = entry.hasData === true;
    if (code === '' || status === 0) continue;
    const def: ErrorDef = { name, code, status, description };
    if (hasData) {
      def.data = errorDataSchema(responses, status, code);
    }
    out.push(def);
  }
  // Deterministic order: by error name.
  out.sort((a, b) => (a.name < b.name ? -1 : a.name > b.name ? 1 : 0));
  return out;
}

// errorDataSchema pulls the data-payload schema out of a declared error's
// response shape. Single error on a status → the standalone schema; multiple
// errors sharing a status → `oneOf`, pick the variant whose
// `error: { const: <code> }` discriminator matches.
function errorDataSchema(
  responses: JsonObject | undefined,
  status: number,
  code: string,
): Schema | undefined {
  const resp = asObject(responses?.[String(status)]);
  const jsonCt = asObject(asObject(resp?.content)?.['application/json']);
  const schema = asObject(jsonCt?.schema);
  if (!schema) return undefined;

  const variants = schema.oneOf;
  if (Array.isArray(variants)) {
    for (const v of variants) {
      const vm = asObject(v);
      if (!vm) continue;
      const errProp = asObject(asObject(vm.properties)?.error);
      if (errProp && asString(errProp.const) === code) {
        return extractDataProperty(vm);
      }
    }
    return undefined;
  }
  return extractDataProperty(schema);
}

function extractDataProperty(schema: JsonObject): Schema | undefined {
  const dm = asObject(asObject(schema.properties)?.data);
  if (!dm) return undefined;
  return parseSchema(dm);
}

function requestSchema(op: JsonObject): Schema | undefined {
  const body = asObject(op.requestBody);
  if (!body) return undefined;
  return schemaFromContent(body.content);
}

// parametersSchemaIn collects the operation's `parameters[in:<where>]` entries
// into a synthetic object Schema (one property per parameter), name-sorted for
// deterministic output. Returns undefined when the op declares no parameter in
// that location; path params are threaded separately (pathParamNames).
function parametersSchemaIn(op: JsonObject, where: 'header' | 'query'): Schema | undefined {
  const paramsRaw = op.parameters;
  if (!Array.isArray(paramsRaw) || paramsRaw.length === 0) return undefined;
  const props: Prop[] = [];
  for (const p of paramsRaw) {
    const pm = asObject(p);
    if (!pm) continue;
    if (asString(pm.in) !== where) continue;
    const name = asString(pm.name);
    if (name === '') continue;
    const required = pm.required === true;
    const sm = asObject(pm.schema);
    const ps = sm ? parseSchema(sm) : emptySchema('string');
    props.push({ name, schema: ps, required });
  }
  if (props.length === 0) return undefined;
  props.sort((a, b) => (a.name < b.name ? -1 : a.name > b.name ? 1 : 0));
  return { ...emptySchema('object'), props };
}

// pathParamNames extracts the `{name}` template segments from an OpenAPI path,
// in left-to-right path order. Empty `{}` is ignored.
/**
 * ACCEPTS BOTH SPELLINGS, because our own runtime writes the other one.
 *
 * OpenAPI templates a path parameter as `{name}`. The palbase backend runtime
 * stamps its contract with `:name` — Express's spelling — so every route with a
 * path parameter came through here with none found, and codegen emitted a
 * method that could not be given the value it needs. Measured against the live
 * control plane: `/v1/panel/projects/:projectId` produced
 * `projectById(options?)`.
 *
 * Normalising at the door is the narrow fix: everything downstream already
 * speaks `{name}`, so only this function has to know there are two spellings.
 */
export function normalizePathTemplate(path: string): string {
  return path.replace(/:([A-Za-z_][A-Za-z0-9_]*)/g, '{$1}');
}

export function pathParamNames(rawPath: string): string[] {
  const path = normalizePathTemplate(rawPath);
  const out: string[] = [];
  let rest = path;
  for (;;) {
    const open = rest.indexOf('{');
    if (open < 0) break;
    const close = rest.indexOf('}', open);
    if (close < 0) break;
    const name = rest.slice(open + 1, close);
    if (name !== '') out.push(name);
    rest = rest.slice(close + 1);
  }
  return out;
}

function responseSchema(op: JsonObject): Schema | undefined {
  const responses = asObject(op.responses);
  if (!responses) return undefined;
  // Prefer 200, then 201, then any other 2xx (sorted).
  const others: string[] = [];
  for (const code of Object.keys(responses)) {
    if (code.startsWith('2') && code !== '200' && code !== '201') others.push(code);
  }
  others.sort();
  for (const code of ['200', '201', ...others]) {
    const resp = asObject(responses[code]);
    if (!resp) continue;
    const s = schemaFromContent(resp.content);
    if (s) return s;
  }
  return undefined;
}

function schemaFromContent(content: unknown): Schema | undefined {
  const jsonCt = asObject(asObject(content)?.['application/json']);
  const schema = asObject(jsonCt?.schema);
  if (!schema) return undefined;
  // Skip $ref'd shared components (error envelope etc.).
  if ('$ref' in schema) return undefined;
  return parseSchema(schema);
}

function emptySchema(kind: SchemaKind): Schema {
  return { kind, nullable: false, props: [], enumVals: [] };
}

export function parseSchema(s: JsonObject): Schema {
  // `type: [<t>, "null"]` MEANS NULLABLE and is read HERE, before the enum
  // branch returns: `z.enum([…]).nullable()` arrives as
  // `{"type":["string","null"],"enum":[…]}`, and reading the type array only
  // further down typed a nullable enum as non-null — a server `null` then
  // broke the app that trusted its generated type.
  let nullable = s.nullable === true || (Array.isArray(s.type) && s.type.includes('null'));

  if (s.const === null) return emptySchema('null');
  if (['string', 'number', 'boolean'].includes(typeof s.const)) {
    return { ...emptySchema('literal'), nullable, literal: s.const as string | number | boolean };
  }
  const alternatives = s.anyOf ?? s.oneOf;
  if (Array.isArray(alternatives) && alternatives.length > 0) {
    const members = alternatives.map((value) => parseSchema(asObject(value) ?? {}));
    nullable ||= members.some((member) => member.kind === 'null');
    const values = members.filter((member) => member.kind !== 'null');
    if (values.length === 0) return emptySchema('null');
    if (values.length === 1) return { ...values[0]!, nullable: nullable || values[0]!.nullable };
    return { ...emptySchema('union'), nullable, members: values };
  }

  const enumRaw = s.enum;
  if (Array.isArray(enumRaw)) {
    const cases: string[] = [];
    let allStrings = true;
    for (const v of enumRaw) {
      if (typeof v === 'string') {
        cases.push(v);
      } else {
        allStrings = false;
        break;
      }
    }
    if (allStrings && cases.length > 0) {
      return { ...emptySchema('enum'), nullable, enumVals: cases };
    }
    if (
      enumRaw.length > 0 &&
      enumRaw.every(
        (value) => value === null || ['string', 'number', 'boolean'].includes(typeof value),
      )
    ) {
      return parseSchema({ anyOf: enumRaw.map((value) => ({ const: value })), nullable });
    }
  }

  // Draft 7 / OpenAPI 3.1 allow `type` as an array — `["string","null"]` is
  // what `zod-to-json-schema` emits for `z.string().nullable()`. Lower it to
  // the single non-null type + nullable=true.
  let typ = asString(s.type);
  if (typ === '' && Array.isArray(s.type)) {
    const types = s.type.filter((value) => typeof value === 'string' && value !== 'null');
    if (types.length > 1) {
      return parseSchema({ anyOf: s.type.map((type) => ({ ...s, type })), nullable });
    }
    for (const v of s.type) {
      if (typeof v !== 'string') continue;
      if (v === 'null') {
        nullable = true;
      } else if (typ === '') {
        typ = v;
      }
    }
  }
  switch (typ) {
    case 'null':
      return emptySchema('null');
    case 'string':
    case 'number':
    case 'integer':
    case 'boolean':
      return { ...emptySchema(typ), nullable };
    case 'array': {
      const items = asObject(s.items);
      const elem = items ? parseSchema(items) : emptySchema('any');
      return {
        ...emptySchema('array'),
        nullable,
        elem,
        ...(s.maxItems === 0 ? { empty: true as const } : {}),
      };
    }
    case 'object':
      return parseObject(s, nullable);
    default:
      if ('properties' in s) return parseObject(s, nullable);
      return { ...emptySchema('any'), nullable };
  }
}

function parseObject(s: JsonObject, nullable: boolean): Schema {
  const propsRaw = asObject(s.properties) ?? {};
  const requiredSet = new Set<string>();
  if (Array.isArray(s.required)) {
    for (const r of s.required) {
      if (typeof r === 'string') requiredSet.add(r);
    }
  }
  const names = Object.keys(propsRaw).sort();
  const props: Prop[] = [];
  for (const name of names) {
    const pm = asObject(propsRaw[name]);
    const ps = pm ? parseSchema(pm) : emptySchema('any');
    props.push({ name, schema: ps, required: requiredSet.has(name) });
  }
  const additional = asObject(s.additionalProperties);
  return {
    ...emptySchema('object'),
    nullable,
    props,
    ...(additional
      ? { additional: parseSchema(additional) }
      : s.additionalProperties === false
        ? { additional: false as const }
        : {}),
  };
}

// ── rooms ────────────────────────────────────────────────────────────────────

/** One `@Room`, read from the DOCUMENT-level `x-palbase-room` extension. */
export interface RoomDecl {
  /** The channel pattern, colon-separated: `ai:{sessionId}`. */
  pattern: string;
  /** The `{param}` names in order — they become named arguments, so nobody
   *  interpolates a topic by hand. */
  params: string[];
  /** Events the SERVER sends. */
  events: RoomPayload[];
  /** Messages a CLIENT may send. */
  messages: RoomPayload[];
}

export interface RoomPayload {
  name: string;
  schema: Schema;
}

/**
 * Read `x-palbase-room` off the document root.
 *
 * DOCUMENT-level, not operation-level, and the difference matters: a room has no
 * verb and no path to hang an extension off. A generator that looks for it
 * beside `x-palbase-sse` finds nothing, emits nothing, and reports success — the
 * failure shape this codebase has already paid for once.
 *
 * A malformed entry is skipped rather than thrown on: a spec this generator
 * cannot fully read must still produce a working client for the parts it can.
 */
export function parseRooms(specText: string): RoomDecl[] {
  let root: JsonObject | undefined;
  try {
    root = asObject(JSON.parse(specText));
  } catch {
    return [];
  }
  const ext = asObject(root?.['x-palbase-room']);
  if (!ext || !root) return [];

  const out: RoomDecl[] = [];
  for (const [pattern, raw] of Object.entries(ext)) {
    const decl = asObject(raw);
    if (!decl) continue;
    out.push({
      pattern,
      params: roomParamNames(pattern),
      events: roomPayloads(decl.events, root),
      messages: roomPayloads(decl.messages, root),
    });
  }
  // Deterministic: a generator whose output depends on key order writes a
  // different file every run and no golden can hold it.
  out.sort((a, b) => (a.pattern < b.pattern ? -1 : a.pattern > b.pattern ? 1 : 0));
  return out;
}

function roomParamNames(pattern: string): string[] {
  const names: string[] = [];
  for (const seg of pattern.split(':')) {
    if (seg.startsWith('{') && seg.endsWith('}') && seg.length > 2) {
      names.push(seg.slice(1, -1));
    }
  }
  return names;
}

function roomPayloads(raw: unknown, root: JsonObject): RoomPayload[] {
  const map = asObject(raw);
  if (!map) return [];
  const out: RoomPayload[] = [];
  for (const [name, entry] of Object.entries(map)) {
    const dict = asObject(entry);
    if (!dict) continue;
    // Room payloads always live in components — the backend registers them there
    // precisely so a shape shared by two rooms travels once.
    const target = resolveRoomRef(dict, root) ?? dict;
    out.push({ name, schema: parseSchema(target) });
  }
  out.sort((a, b) => (a.name < b.name ? -1 : a.name > b.name ? 1 : 0));
  return out;
}

function resolveRoomRef(s: JsonObject, root: JsonObject): JsonObject | undefined {
  const ref = asString(s.$ref);
  if (!ref.startsWith('#/')) return undefined;
  let node: unknown = root;
  for (const rawPart of ref.slice(2).split('/')) {
    const part = rawPart.replace(/~1/g, '/').replace(/~0/g, '~');
    const dict = asObject(node);
    if (!dict || !(part in dict)) return undefined;
    node = dict[part];
  }
  return asObject(node);
}
